import type { GameEvent } from '../schema';

export const SOCIAL_EVENTS: GameEvent[] = [
  {
    id: 'social.newFriend',
    title: 'An unexpected friendship',
    description: 'Someone you keep running into. It would take effort to turn this into an actual friendship.',
    category: 'social',
    pools: ['pool.social', 'pool.young'],
    weight: 8,
    cooldown: 2,
    weightMods: [
      { conditions: [{ k: 'trait', trait: 'socialConfidence', op: '>=', value: 55 }], multiplier: 1.4 },
      { conditions: [{ k: 'num', path: 'friendCount', op: '>=', value: 5 }], multiplier: 0.5 },
      { conditions: [{ k: 'num', path: 'lifeLoad', op: '>=', value: 75 }], multiplier: 0.55 },
    ],
    choices: [
      {
        id: 'invest',
        label: 'Make the effort',
        hint: 'safe',
        tags: ['social'],
        outcomes: [
          {
            weight: 1,
            label: 'A new friend',
            narrative: 'Adult friendships require scheduling. You did the scheduling.',
            tone: 'positive',
            effects: [
              { k: 'person', op: 'add', kind: 'friend', tags: ['friend'] },
              { k: 'stat', stat: 'happiness', delta: 7 },
              { k: 'habit', habit: 'socialising', delta: 8 },
              { k: 'skill', skill: 'social', delta: 3 },
              { k: 'aspiration', op: 'progress', defId: 'goal.closeFriends', delta: 25 },
            ],
          },
        ],
      },
      {
        id: 'drift',
        label: 'Stay friendly and busy',
        hint: 'safe',
        outcomes: [
          {
            weight: 1,
            label: 'Stayed acquaintances',
            narrative: 'Perfectly pleasant, permanently shallow.',
            tone: 'neutral',
            effects: [{ k: 'habit', habit: 'socialising', delta: -3 }],
          },
        ],
      },
    ],
  },
  {
    id: 'social.neglect',
    title: 'You have not replied in months',
    description: 'A friend has stopped initiating. You noticed, which is the uncomfortable part.',
    category: 'social',
    pools: ['pool.social', 'pool.relationship.strain'],
    weight: 7,
    cooldown: 3,
    requires: [{ k: 'hasPerson', kind: 'friend' }],
    weightMods: [
      { conditions: [{ k: 'habit', habit: 'relationshipCare', op: '<=', value: 40 }], multiplier: 1.9 },
      { conditions: [{ k: 'num', path: 'lifeLoad', op: '>=', value: 68 }], multiplier: 1.6 },
    ],
    choices: [
      {
        id: 'reach',
        label: 'Reach out properly',
        hint: 'safe',
        tags: ['social', 'repair'],
        outcomes: [
          {
            weight: 74,
            label: 'Picked up where you left off',
            narrative: 'Three hours and it was as though nothing had lapsed. Some friendships are load-bearing.',
            tone: 'positive',
            effects: [
              { k: 'relationship', who: { r: 'kind', kind: 'friend', pick: 'weakest' }, dim: 'closeness', delta: 18 },
              { k: 'relationship', who: { r: 'kind', kind: 'friend', pick: 'weakest' }, dim: 'trust', delta: 10 },
              { k: 'habit', habit: 'relationshipCare', delta: 10 },
              { k: 'stat', stat: 'happiness', delta: 8 },
            ],
          },
          {
            weight: 26,
            label: 'Too much time had passed',
            narrative: 'Courteous, brief, and clearly a conclusion rather than a resumption.',
            tone: 'negative',
            effects: [
              { k: 'relationship', who: { r: 'kind', kind: 'friend', pick: 'weakest' }, dim: 'closeness', delta: -10 },
              { k: 'stat', stat: 'happiness', delta: -5 },
              { k: 'regret', op: 'add', id: 'regret.friend', label: 'Let a friendship die', weight: 14 },
            ],
          },
        ],
      },
      {
        id: 'nothing',
        label: 'Let it fade',
        hint: 'risky',
        tags: ['avoidance'],
        outcomes: [
          {
            weight: 1,
            label: 'It faded',
            narrative: 'No argument, no event. Just a slow decline in the frequency of everything.',
            tone: 'negative',
            effects: [
              { k: 'relationshipAll', kind: 'friend', dim: 'closeness', delta: -12 },
              { k: 'habit', habit: 'relationshipCare', delta: -8 },
              { k: 'counter', name: 'lostFriendships', delta: 1 },
            ],
          },
        ],
      },
    ],
  },
  {
    id: 'social.networkHelp',
    title: 'Someone in your network can open a door',
    description: 'A single introduction would change your options. Asking means admitting you need something.',
    category: 'social',
    pools: ['pool.social', 'pool.opportunity'],
    weight: 6,
    cooldown: 4,
    requires: [{ k: 'num', path: 'socialHealth', op: '>=', value: 38 }],
    choices: [
      {
        id: 'ask',
        label: 'Ask',
        hint: 'mixed',
        tags: ['social', 'courage'],
        outcomes: [
          {
            weight: 70,
            label: 'They helped',
            narrative: 'They were pleased to be asked, which is almost always true and almost never believed.',
            tone: 'positive',
            favouredBy: [{ conditions: [{ k: 'num', path: 'supportNetwork', op: '>=', value: 58 }], multiplier: 1.6 }],
            effects: [
              { k: 'career', op: 'adjust', field: 'reputation', delta: 8 },
              { k: 'triggerEvent', eventId: 'opportunity.introduction' },
              { k: 'relationshipAll', dim: 'trust', delta: 4 },
            ],
          },
          {
            weight: 30,
            label: 'Nothing came of it',
            narrative: 'Warm words and no action. The network is not a vending machine.',
            tone: 'neutral',
            effects: [{ k: 'trait', trait: 'socialConfidence', delta: -2 }],
          },
        ],
      },
      {
        id: 'dont',
        label: 'Do it on your own',
        hint: 'safe',
        tags: ['independence'],
        outcomes: [
          {
            weight: 1,
            label: 'Did not ask',
            narrative: 'Self-sufficiency is admirable and occasionally expensive.',
            tone: 'neutral',
            effects: [{ k: 'counter', name: 'missedOpportunities', delta: 1 }, { k: 'trait', trait: 'conscientiousness', delta: 2 }],
          },
        ],
      },
    ],
  },
  {
    id: 'social.relocation',
    title: 'A reason to move city',
    description: 'A better job, or a partner, or simply a different life. It would cost you every casual friendship you have.',
    category: 'social',
    pools: ['pool.social', 'pool.opportunity'],
    weight: 5,
    cooldown: 10,
    requires: [{ k: 'age', min: 22, max: 58 }],
    choices: [
      {
        id: 'move',
        label: 'Move',
        hint: 'mixed',
        tags: ['change', 'exploration'],
        outcomes: [
          {
            weight: 1,
            label: 'Relocated',
            narrative: 'Higher salary, unfamiliar streets, and eighteen months of eating alone.',
            tone: 'neutral',
            effects: [
              { k: 'salary', mode: 'mult', value: 1.18 },
              { k: 'relationshipAll', kind: 'friend', dim: 'closeness', delta: -18 },
              { k: 'relationshipAll', kind: 'parent', dim: 'closeness', delta: -10 },
              { k: 'stat', stat: 'happiness', delta: -6 },
              { k: 'trait', trait: 'adaptability', delta: 7 },
              { k: 'counter', name: 'relocations', delta: 1 },
              { k: 'fixedExpenses', mode: 'mult', value: 1.16 },
              { k: 'schedule', afterYears: 3, label: 'Built a new life', effects: [{ k: 'person', op: 'add', kind: 'friend', tags: ['new-city'] }, { k: 'stat', stat: 'happiness', delta: 9 }] },
            ],
          },
        ],
      },
      {
        id: 'stay',
        label: 'Stay where your people are',
        hint: 'safe',
        tags: ['social', 'roots'],
        outcomes: [
          {
            weight: 1,
            label: 'Stayed',
            narrative: 'You turned down more money to keep the people who know your history.',
            tone: 'positive',
            effects: [
              { k: 'relationshipAll', kind: 'friend', dim: 'closeness', delta: 8 },
              { k: 'stat', stat: 'happiness', delta: 6 },
              { k: 'counter', name: 'missedOpportunities', delta: 1 },
            ],
          },
        ],
      },
    ],
  },
  {
    id: 'social.communityRole',
    title: 'They want you to run it',
    description: 'A local organisation needs someone dependable. It is unpaid and genuinely useful.',
    category: 'social',
    pools: ['pool.social', 'pool.legacy', 'pool.retired.leisure'],
    weight: 5,
    cooldown: 5,
    requires: [{ k: 'age', min: 30 }],
    choices: [
      {
        id: 'take',
        label: 'Take it on',
        hint: 'mixed',
        tags: ['contribution', 'social'],
        outcomes: [
          {
            weight: 1,
            label: 'Became the person who runs it',
            narrative: 'Endless small admin, a real effect on people nearby, and no compensation of any kind.',
            tone: 'positive',
            effects: [
              { k: 'stat', stat: 'purpose', delta: 13 },
              { k: 'counter', name: 'contributions', delta: 2 },
              { k: 'aspiration', op: 'progress', defId: 'dream.contribute', delta: 28 },
              { k: 'person', op: 'add', kind: 'friend', tags: ['community'] },
              { k: 'stat', stat: 'energy', delta: -7 },
              { k: 'skill', skill: 'management', delta: 5 },
            ],
          },
        ],
      },
      {
        id: 'decline',
        label: 'Decline',
        hint: 'safe',
        outcomes: [
          {
            weight: 1,
            label: 'Said no',
            narrative: 'Someone less busy did it. You were relieved and marginally diminished.',
            tone: 'neutral',
            effects: [{ k: 'stat', stat: 'energy', delta: 3 }],
          },
        ],
      },
    ],
  },
  {
    id: 'social.oldFriendLoss',
    title: 'A friend dies',
    description: 'Too early. It reorganises your sense of how much time is a given.',
    category: 'social',
    pools: ['pool.health.late', 'pool.retired.family'],
    weight: 4,
    cooldown: 6,
    requires: [{ k: 'hasPerson', kind: 'friend' }, { k: 'age', min: 52 }],
    choices: [
      {
        id: 'respond',
        label: 'Let it change something',
        hint: 'safe',
        tags: ['reflection'],
        outcomes: [
          {
            weight: 1,
            label: 'A reordering',
            narrative: 'You called three people you had been meaning to call for years.',
            tone: 'negative',
            effects: [
              { k: 'person', op: 'remove', who: { r: 'kind', kind: 'friend', pick: 'closest' }, reason: 'died' },
              { k: 'stat', stat: 'happiness', delta: -14 },
              { k: 'stat', stat: 'purpose', delta: 8 },
              { k: 'relationshipAll', dim: 'closeness', delta: 8 },
              { k: 'habit', habit: 'relationshipCare', delta: 12 },
              { k: 'memory', title: 'Lost a close friend', tone: 'negative', category: 'relationship', weight: 9 },
            ],
          },
        ],
      },
      {
        id: 'quiet',
        label: 'Absorb it quietly',
        hint: 'safe',
        outcomes: [
          {
            weight: 1,
            label: 'Grieved privately',
            narrative: 'You did not talk about it. It sat there anyway.',
            tone: 'negative',
            effects: [
              { k: 'person', op: 'remove', who: { r: 'kind', kind: 'friend', pick: 'closest' }, reason: 'died' },
              { k: 'stat', stat: 'happiness', delta: -16 },
              { k: 'stat', stat: 'stress', delta: 8 },
            ],
          },
        ],
      },
    ],
  },
];
